import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchMegaphoneCampaigns } from '@services/index';
import { PaginatedResponse } from '@app/types/common';
import { MegaphoneCampaign } from '@insights_module/types/Megaphone';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useDebouncedState } from '@app/hooks/useDebounce';

export const useInfiniteMegaphoneCampaigns = () => {
  const { isEnabled: isMegaphoneTrackingPixelEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_TRACKING_PIXEL
  );
  const [totalMegaphoneCampaigns, setTotalMegaphoneCampaigns] = useState<number>(0);
  const [campaigns, setCampaigns] = useState<MegaphoneCampaign[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const debouncedKeyword = useDebouncedState(keyword, 250);

  const { data, isLoading, error, isFetching } = useInfiniteQuery({
    queryKey: ['megaphone-campaigns', debouncedKeyword],
    queryFn: ({ pageParam }) =>
      fetchMegaphoneCampaigns({ page: pageParam, pageSize: 500, keyword: debouncedKeyword }),
    getNextPageParam: ({ pagination, pageParam }) => {
      const { total, page_size } = pagination;
      const hasMore = total / page_size > pageParam;
      return hasMore ? pageParam + 1 : undefined;
    },
    getPreviousPageParam: ({ pageParam }) => {
      return pageParam > 1;
    },
    keepPreviousData: true,
    staleTime: 60 * 60 * 1000, // 1h
    enabled: isMegaphoneTrackingPixelEnabled,
  });

  useEffect(() => {
    if (data?.pages?.length) {
      setTotalMegaphoneCampaigns(data.pages[0].pagination.total);
      const arr: MegaphoneCampaign[] = [];
      data.pages?.forEach((element: PaginatedResponse<MegaphoneCampaign>) => {
        element.data.map((item: MegaphoneCampaign) => arr.push(item));
      });

      setCampaigns(arr);
    } else {
      setCampaigns([]);
    }
  }, [data?.pages]);

  return {
    campaigns,
    totalMegaphoneCampaigns,
    isLoading: isLoading || isFetching,
    error,
    keyword,
    setKeyword,
  };
};
